import type { Registry as Services } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type {
  Period,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { RestrictionOverridePayload } from 'lumen/services/repositories/restriction-action';
import {
  MIN_LOS_DEFAULT_VALUE,
  MIN_LOS_NO_RESTRICTION_VALUE,
  type MinLosRestrictionType,
  OVERRIDE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  RateGroupContextFields,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';
import {
  hasRestrictionActionChanged,
  minLosRestrictionValues,
  minLosTypeToValue,
  minLosValueToType,
} from 'lumen/utils/pricing-assistant/restriction-action-utils';
import {
  formatMinLosRecommendation,
  formatMinLosValue,
  minLosErrorMessage,
} from 'lumen/utils/pricing-assistant/restriction-display-utils';
import type { RestrictionValues } from 'lumen/utils/pricing-assistant/restriction-values-utils';

export type SendRestrictionConfig = RateGroupContextFields & {
  existingOverride: MinLosOverride | OverbookingOverride | null | undefined;
  getRestrictionValues: () => RestrictionActionValue[] | null;
  hasError: () => boolean;
  markTouched: () => void;
  overrideRemoved: boolean;
  overrideType: RestrictionOverridePayload['type'];
  overrideValue: number | null;
  restriction: Restriction | undefined;
  restrictionRecommendation: RestrictionRecommendation | undefined;
};

interface MinLosRestrictionStateOptions {
  /* Identifies the rate group and is echoed into the send config. */
  contextFields: RateGroupContextFields;
  /* A thunk rather than the period itself so reads follow the modal's @period arg if
     the caller replaces it; the modal's integration tests simulate a re-request that
     way. */
  getPeriod: () => Period;
  intl: Services['intl'];
  /* Modal-wide send-phase signals; see the modal's isPreparingSend /
     isPushingRestrictionAction getters for what each phase covers. */
  isPreparingSend: () => boolean;
  isPushingRestrictionAction: () => boolean;
}

/* Form state for one rate group's MinLOS restriction in the day-override-modal.
   Owns the user's unsaved edits and derives everything else live from Period, so
   the modal holds a collection of instances instead of a bespoke set of variables
   per restriction type.

   Instances must be created once per modal open (the modal's constructor):
   initiallyHadOverride snapshots there, and rebuilding an instance mid-send would
   reset it — see the overrideRemoved getter. */
export default class MinLosRestrictionState {
  @tracked automationOnUserEdit?: boolean;
  @tracked restrictionTypeUserEdit?: MinLosRestrictionType;
  @tracked touched = false;
  @tracked userEdit?: number | null;

  /* Snapshot at modal open — read live it would flip after the override delete + the
     restrictions re-request, breaking overrideRemoved mid-send. Tracks the override
     record, not showOverride, so turning automation on also deletes a failed override. */
  readonly initiallyHadOverride: boolean;

  onBlur = (): void => {
    this.touched = true;
  };
  onTypeChange = (event: Event): void => {
    /* The value input is intentionally not updated — its value is preserved so
       switching back to MIN_LOS restores whatever the user had typed before. */
    this.restrictionTypeUserEdit = (event.target as HTMLInputElement)
      .value as MinLosRestrictionType;
  };
  private readonly options: MinLosRestrictionStateOptions;

  reset = (): void => {
    this.userEdit = undefined;
    this.restrictionTypeUserEdit = undefined;
  };
  toggleAutomation = (event: Event): void => {
    const checked = (event.target as HTMLInputElement).checked;
    this.automationOnUserEdit = checked;
    this.reset();

    if (!checked) {
      this.touched = false;
    }
  };

  updateValue = (value: number | null): void => {
    this.userEdit = value;
  };

  get automationOn(): boolean {
    return resolveUserEdit(this.automationOnUserEdit, this.savedAutomationOn);
  }

  /* True when the sendable state differs from what is in place, i.e. the user actually
     changed it. Baselines against what an untouched form would send, so the
     comparison stays in step with what the input shows. */
  get changed(): boolean {
    return hasRestrictionActionChanged(
      {
        automationOn: this.automationOn,
        type: this.restrictionType,
        value: minLosTypeToValue(this.restrictionType, this.value),
      },
      {
        automationOn: this.savedAutomationOn,
        type: this.seedType,
        value: minLosTypeToValue(this.seedType, this.seedValue),
      },
    );
  }

  /* An untouched input shows no error. */
  get errorMessage(): string | null {
    return this.touched ? this.validationError : null;
  }

  /* The MinLOS currently applied in the PMS. */
  get formattedCurrentValue(): string {
    return formatMinLosValue({
      intl: this.options.intl,
      value: this.restriction?.minLosValue,
    });
  }

  get formattedRecommendation(): string {
    return formatMinLosRecommendation({
      displayedValue: this.restriction?.minLosValue,
      intl: this.options.intl,
      recommendedValue: this.restrictionRecommendation?.minLosValue,
    });
  }

  get hasActionInProgress(): boolean {
    return this.period.hasMinLosActionInProgress(this.options.contextFields);
  }

  /* The three checks map to the three phases a send goes through, each gated on this
     instance's own signals so a sibling's action never drives it:
     1. `overrideRemoved && isPreparingSend` — deleting the override (automation
        checkbox ticked) and the shared restrictions/recommendations re-request.
     2. `willSend && isPushingRestrictionAction` — the createRestrictionAction POST is
        in flight; `hasActionInProgress` is not active yet since it relies on the
        restriction-action record landing in the store.
     3. `hasActionInProgress` — the restriction-action has landed and its pollers have
        started. */
  get isLoading(): boolean {
    return (
      (this.overrideRemoved && this.options.isPreparingSend()) ||
      (this.willSend && this.options.isPushingRestrictionAction()) ||
      this.hasActionInProgress
    );
  }

  /* What the MinLOS column shows before any edit: the recommendation under automation,
     otherwise the value in effect, as is the PMS value after a failed push. Never the
     default the input starts at, which is not a restriction. Reads the saved automation
     state, not the user's edit. */
  get minLosColumnValue(): number | null {
    return this.savedAutomationOn && !this.showException
      ? (this.restrictionRecommendation?.minLosValue ?? null)
      : this.values.inEffectValue;
  }

  /* True when the user has toggled automation ON from an initial OFF state, meaning
     the existing override will be deleted on send. */
  get overrideRemoved(): boolean {
    return this.automationOn && this.initiallyHadOverride;
  }

  /* Read through getPeriod on every access so values stay live through the delete →
     re-request → push sequence in the modal's send flow, where restriction records
     are unloaded and re-fetched. */
  private get period(): Period {
    return this.options.getPeriod();
  }

  get restriction(): Restriction | undefined {
    return this.period.restrictionFor(this.options.contextFields);
  }

  get restrictionRecommendation(): RestrictionRecommendation | undefined {
    return this.period.restrictionRecommendationFor(this.options.contextFields);
  }

  /* Like value, the user's pick over the live seed, so an untouched form follows a
     re-requested recommendation instead of drifting from the baseline in changed. */
  get restrictionType(): MinLosRestrictionType {
    return resolveUserEdit(this.restrictionTypeUserEdit, this.seedType);
  }

  private get savedAutomationOn(): boolean {
    return (
      this.restriction?.minLosRestrictionValueObject?.autopilotEnabled ?? false
    );
  }

  /* The radio type an untouched form starts on, from the value in effect. */
  private get seedType(): MinLosRestrictionType {
    return minLosValueToType(this.values.inEffectValue);
  }

  /* The MinLOS input's starting value: the value in effect, or the MIN_LOS default
     when there is none or it is the no-restriction sentinel, so switching to MinLOS
     starts at a valid value. */
  private get seedValue(): number {
    const value = this.values.inEffectValue;

    return value == null || value === MIN_LOS_NO_RESTRICTION_VALUE
      ? MIN_LOS_DEFAULT_VALUE
      : value;
  }

  get showException(): boolean {
    return this.values.hasFailedPush;
  }

  get showOverride(): boolean {
    return this.values.showOverride;
  }

  /* Validity regardless of touched; errorMessage gates it on touched. Automation has
     no value to validate. */
  get validationError(): string | null {
    if (this.automationOn) {
      return null;
    }

    return minLosErrorMessage({
      intl: this.options.intl,
      restrictionType: this.restrictionType,
      value: this.value,
    });
  }

  get value(): number | null {
    return resolveUserEdit(this.userEdit, this.seedValue);
  }

  /* The period's resolved MinLOS values for this rate group; see RestrictionValues. */
  private get values(): RestrictionValues {
    return this.period.minLosValues(this.options.contextFields);
  }

  /* True when this instance's change will be sent on the next push, covering both a
     removal and a value change with automation off. */
  get willSend(): boolean {
    return this.changed && (!this.automationOn || this.overrideRemoved);
  }

  constructor(options: MinLosRestrictionStateOptions) {
    this.options = options;
    this.initiallyHadOverride = this.period.hasMinLosOverride(
      this.options.contextFields,
    );
  }

  toSendConfig(): SendRestrictionConfig {
    return {
      ...this.options.contextFields,
      existingOverride: this.restriction?.minLosOverride,
      getRestrictionValues: () =>
        minLosRestrictionValues(
          this.overrideRemoved,
          this.restrictionRecommendation?.minLosValue,
          this.restrictionType,
          this.value,
        ),
      hasError: () => this.errorMessage !== null,
      markTouched: (): void => {
        this.touched = true;
      },
      overrideRemoved: this.overrideRemoved,
      overrideType: OVERRIDE_TYPES.MIN_LOS,
      overrideValue: minLosTypeToValue(this.restrictionType, this.value),
      restriction: this.restriction,
      restrictionRecommendation: this.restrictionRecommendation,
    };
  }
}

/* Returns the user's edit when present, otherwise the value from the API. `undefined`
   means "not edited"; a `null`/`false` edit is a real value the user set, so we test
   against `undefined` rather than falling back with `??`. */
function resolveUserEdit<T>(userEdit: T | undefined, liveValue: T): T {
  return userEdit === undefined ? liveValue : userEdit;
}
